import { Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";
import { useTranslation } from "@/i18n";

interface StepProps {
  index: number;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}

function Step({ index, active, title, children }: StepProps) {
  return (
    <div className="rounded-md border border-ocean-100 bg-white p-4">
      <div className="flex items-start gap-3">
        <div
          className={
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-medium " +
            (active
              ? "bg-ocean-600 text-white"
              : "bg-ocean-100 text-ocean-600")
          }
        >
          {index}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-ocean-900">{title}</div>
          <div className="mt-2 text-xs text-neutral-600">{children}</div>
        </div>
      </div>
    </div>
  );
}

// Pulled verbatim from the backend BFSI starter template
// ``finance-external-llm-restriction`` so the curl sample matches
// what an operator would copy out of the dropdown in the create
// dialog. Framework mapping included to signal compliance-readiness
// to a procurement reader.
const SAMPLE_RULE = `{
  "name": "Finance Team External LLM Restriction",
  "description": "Block external LLM untuk data finance klasifikasi confidential/restricted.",
  "when": {
    "operator": "and",
    "conditions": [
      {"field": "user.team", "operator": "in", "value": ["finance", "treasury"]},
      {"field": "request.model_provider", "operator": "equals", "value": "external"},
      {"field": "context.data_classification", "operator": "in", "value": ["confidential", "restricted"]}
    ]
  },
  "effect": {
    "type": "deny",
    "deny_reason_code": "FINANCE_EXTERNAL_LLM_CONFIDENTIAL",
    "deny_user_message": "Data finance klasifikasi confidential/restricted tidak boleh diproses via external LLM."
  },
  "priority": 90,
  "framework_mapping": [
    {"framework": "ISO_42001", "clause": "A.7.4"},
    {"framework": "UU_PDP_2022", "clause": "Pasal 16-22"}
  ]
}`;

interface Props {
  onCreateClick: () => void;
}

export function PoliciesQuickstart({ onCreateClick }: Props) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-ocean-100 bg-ocean-50/40 p-5 desktop:p-7">
      <h2 className="text-lg font-medium text-ocean-900">
        {t("policies.quickstart.title")}
      </h2>
      <p className="mt-1 max-w-2xl text-sm text-neutral-600">
        {t("policies.quickstart.description")}
      </p>

      <div className="mt-5 space-y-3">
        <Step index={1} active title={t("policies.quickstart.step1Title")}>
          {t("policies.quickstart.step1Body")}
          <div className="mt-3">
            <CodeBlock language="json">{SAMPLE_RULE}</CodeBlock>
          </div>
        </Step>

        <Step index={2} title={t("policies.quickstart.step2Title")}>
          {t("policies.quickstart.step2Body")}
          <div className="mt-3">
            <Button size="sm" onClick={onCreateClick}>
              <Plus size={14} />
              {t("policies.actions.create")}
            </Button>
          </div>
        </Step>

        <Step index={3} title={t("policies.quickstart.step3Title")}>
          {t("policies.quickstart.step3Body")}
        </Step>
      </div>
    </div>
  );
}
